import { HStack, IconButton, Text, Flex, Box, Menu, MenuButton, MenuList, MenuItem, Avatar, Divider, Button, Breadcrumb, BreadcrumbItem, BreadcrumbLink, useColorMode } from '@chakra-ui/react'
import { Menu as MenuIcon, Bell, HelpCircle, LogOut, User, Moon, Sun } from 'lucide-react'
import { User as AppUser } from '@/types'
import { useLocation, Link } from 'react-router-dom'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

interface HeaderProps {
  onToggle: () => void
  isCollapsed?: boolean
  user: AppUser | null
  onLogout: () => void
}

export function Header({ onToggle, isCollapsed = false, user, onLogout }: HeaderProps) {
  const colors = useThemeColors()
  const location = useLocation()
  const pathname = location.pathname
  const segments = (pathname || '/').split('/').filter(Boolean)
  const toLabel = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const title = segments.length > 0 ? toLabel(segments[0]) : 'Dashboard'
  const crumbs = segments.map((seg, idx) => ({
    label: toLabel(seg),
    href: '/' + segments.slice(0, idx + 1).join('/'),
  }))
  const { colorMode, toggleColorMode } = useColorMode()
  
  return (
    <Box bg={colors.cardBg} borderBottom="1px" borderColor={colors.border} px={6} py={4}>
      <Flex justify="space-between" align="center">
        <HStack align="start" spacing={3}>
          <IconButton
            aria-label="Toggle sidebar"
            icon={isCollapsed ? <MenuIcon /> : <MenuIcon />}
            variant="ghost"
            display="flex"
            onClick={onToggle}
          />
          <Box display={{ base: 'none', md: 'block' }}>
            <Text fontSize="lg" fontWeight="semibold">{title}</Text>
            {segments.length > 0 && (
              <Breadcrumb fontSize="sm" color={colors.text} separator="/">
                <BreadcrumbItem key="home">
                  <BreadcrumbLink as={Link} to="/dashboard">Home</BreadcrumbLink>
                </BreadcrumbItem>
                {crumbs.map((c, i) => (
                  <BreadcrumbItem key={c.href} isCurrentPage={i === crumbs.length - 1}>
                    {i === crumbs.length - 1 ? (
                      <Text>{c.label}</Text>
                    ) : (
                      <BreadcrumbLink as={Link} to={c.href}>{c.label}</BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                ))}
              </Breadcrumb>
            )}
          </Box>
        </HStack>

        <HStack spacing={2}>
          <IconButton 
            aria-label="Toggle color mode" 
            icon={colorMode === 'light' ? <Moon size={18} /> : <Sun size={18} />} 
            variant="ghost"
            onClick={toggleColorMode}
          />
          <IconButton aria-label="Notifications" icon={<Bell />} variant="ghost" />
          <IconButton
            aria-label="Help"
            icon={<HelpCircle size={18} />}
            variant="ghost"
            as={Link}
            to="/help-center"
          />
          <Menu>
            <MenuButton as={Button} variant="ghost" leftIcon={<Avatar size="sm" name={user?.user_login || ''} />}>
              {user?.user_login ? user.user_login.split(' ')[0] : ''}
            </MenuButton>
            <MenuList>
              <MenuItem icon={<User />} as={Link} to="/profile">Profile</MenuItem>
              <MenuItem icon={<HelpCircle size={16} />} as={Link} to="/help-center">Help Center</MenuItem>
              <Divider />
              <MenuItem icon={<LogOut />} onClick={onLogout}>
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </Box>
  )
}

export default Header

